/**
 * i18n 纯逻辑（不含 React / DOM），因此可被 tsc 编译后在 node 中直接单测。
 * 验证脚本：node scripts/check-i18n.mjs
 * 
 * [gemini-i18n-core] 升级支持 8 国语言矩阵（中、英 + 越、泰、印尼、马来、高棉/柬、日）及多层优雅回退机制
 */

export type Locale = 'zh' | 'en' | 'vi' | 'th' | 'id' | 'ms' | 'km' | 'ja';
export const LOCALES: Locale[] = ['zh', 'en', 'vi', 'th', 'id', 'ms', 'km', 'ja'];
export const DEFAULT_LOCALE: Locale = 'zh';

/** [gemini-i18n-core] 多语言对象结构，允许向后兼容 BiText */
export interface MultiText {
  zh?: string | null;
  en?: string | null;
  vi?: string | null;
  th?: string | null;
  id?: string | null;
  ms?: string | null;
  km?: string | null;
  ja?: string | null;
}

/** 兼容旧代码 BiText 命名 */
export type BiText = MultiText;

/** [gemini-i18n-core] 站点级文案类型：中英必须完全成对，扩展语言可选并具备自动回落 */
export type SiteMessages = Partial<Record<Locale, Record<string, string>>> & {
  zh: Record<string, string>;
  en: Record<string, string>;
};

/** [gemini-i18n-core] 语言元数据定义与本国语自称 */
export interface LocaleMeta {
  code: Locale;
  name: string;      // 本国语言自称（如 Tiếng Việt）
  label: string;     // 中文简称（如 越南语）
  flag: string;      // 国旗标志 Emoji
}

export const LOCALE_CONFIG: Record<Locale, LocaleMeta> = {
  zh: { code: 'zh', name: '简体中文', label: '中文', flag: '🇨🇳' },
  en: { code: 'en', name: 'English', label: 'EN', flag: '🇬🇧' },
  vi: { code: 'vi', name: 'Tiếng Việt', label: '越语', flag: '🇻🇳' },
  th: { code: 'th', name: 'ไทย', label: '泰语', flag: '🇹🇭' },
  id: { code: 'id', name: 'Bahasa Indonesia', label: '印尼语', flag: '🇮🇩' },
  ms: { code: 'ms', name: 'Bahasa Melayu', label: '马来语', flag: '🇲🇾' },
  km: { code: 'km', name: 'ភាសាខ្មែរ', label: '高棉语', flag: '🇰🇭' },
  ja: { code: 'ja', name: '日本語', label: '日语', flag: '🇯🇵' },
};

/** 从 hash 中解析语言前缀，未指定则回落默认语言。
 *  '#/en/ticketing' → { locale: 'en', path: '/ticketing' }
 *  '#/vi/services'  → { locale: 'vi', path: '/services' }
 *  '#/ticketing'    → { locale: 'zh', path: '/ticketing' } */
export function parseHash(hash: string): { locale: Locale; path: string } {
  const raw = hash.replace(/^#/, '').trim();
  if (raw === '' || raw === '/') return { locale: DEFAULT_LOCALE, path: '/' };

  const parts = raw.split('/').filter((p) => p.length > 0);
  const first = parts[0]?.toLowerCase();

  if (LOCALES.includes(first as Locale)) {
    const rest = '/' + parts.slice(1).join('/');
    return { locale: first as Locale, path: rest === '/' ? '/' : rest.replace(/\/+$/, '') };
  }
  return { locale: DEFAULT_LOCALE, path: raw.replace(/\/+$/, '') };
}

/** 反向构造 hash。默认语言不带前缀，保持链接简洁。 */
export function buildHash(locale: Locale, path: string): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === '/' ? `/${locale}` : `/${locale}${clean}`;
}

/** 语言切换时保留当前路径与查询串（如 #/verify?token=xxx）。 */
export function switchLocaleInHash(hash: string, locale: Locale): string {
  const raw = hash.replace(/^#/, '');
  const queryAt = raw.indexOf('?');
  const query = queryAt >= 0 ? raw.slice(queryAt) : '';
  const withoutQuery = queryAt >= 0 ? raw.slice(0, queryAt) : raw;
  const { path } = parseHash(`#${withoutQuery}`);
  return `#${buildHash(locale, path)}${query}`;
}

/** [gemini-i18n-core] 升级多语言回退引擎：当前语种 -> 英文(en) -> 中文(zh) -> 空，避免任何空白 */
export function pick(text: BiText | null | undefined, locale: Locale): string {
  if (!text) return '';
  const direct = text[locale];
  if (direct && direct.trim().length > 0) return direct;
  if (locale !== 'en' && text.en && text.en.trim().length > 0) return text.en;
  if (locale !== 'zh' && text.zh && text.zh.trim().length > 0) return text.zh;
  return text.en || text.zh || '';
}

/** [gemini-msg-global] 共享 UI 文案 8 国语言矩阵覆盖 */
export const MESSAGES: Record<Locale, Record<string, string>> = {
  zh: {
    'legal.name': '天津安迪文化传播有限公司',
    'legal.taxLabel': '统一社会信用代码',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': '天津市红桥区洪湖南路25号209室',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': '越语',
    'lang.th': '泰语',
    'lang.id': '印尼语',
    'lang.ms': '马来语',
    'lang.km': '高棉语',
    'lang.ja': '日语',
    'lang.switchTo': '切换语言',
    'state.loading': '加载中…',
    'error.title': '数据加载失败',
    'error.hint': '请确认后端已启动：./dev.sh start（API 在 22670）',
    'meta.missing': '缺少数据口径',
    'meta.mock': '参考数据',
    'meta.forecast': '预测数据',
    'meta.actual': '真实数据（脱敏）',
    'meta.asOf': '截至',
    'meta.source': '来源',
    'footer.line1':
      '本站信息仅供参考，不构成投资建议或要约。前瞻性陈述存在不确定性。政策金额以主管部门最终核定为准。',
    'footer.line2':
      'Information on this site is for reference only and does not constitute investment advice or an offer. Forward-looking statements involve uncertainty. Policy amounts are subject to final confirmation by the competent authorities.',
    'nav.otherSites': '其他站点',
    'map.schematic': '按经纬度等比投影（无底图）',
    'map.north': 'N ↑',
  },
  en: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': 'Unified Social Credit Code',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': 'Room 209, No. 25 Honghu South Road, Hongqiao District, Tianjin',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': 'Switch language',
    'state.loading': 'Loading…',
    'error.title': 'Failed to load data',
    'error.hint': 'Make sure the backend is running: ./dev.sh start (API on port 22670)',
    'meta.missing': 'Missing data provenance',
    'meta.mock': 'Reference data',
    'meta.forecast': 'Forecast',
    'meta.actual': 'Actual (anonymised)',
    'meta.asOf': 'as of',
    'meta.source': 'source',
    'footer.line1':
      '本站信息仅供参考，不构成投资建议或要约。前瞻性陈述存在不确定性。政策金额以主管部门最终核定为准。',
    'footer.line2':
      'Information on this site is for reference only and does not constitute investment advice or an offer. Forward-looking statements involve uncertainty. Policy amounts are subject to final confirmation by the competent authorities.',
    'nav.otherSites': 'Other sites',
    'map.schematic': 'Equal-scale coordinate projection (no basemap)',
    'map.north': 'N ↑',
  },
  vi: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': 'Mã số tín dụng xã hội thống nhất',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': 'Phòng 209, số 25 đường Hồng Hồ Nam, Quận Hồng Kiều, Thiên Tân',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': 'Chuyển đổi ngôn ngữ',
    'state.loading': 'Đang tải…',
    'error.title': 'Tải dữ liệu thất bại',
    'error.hint': 'Vui lòng xác nhận dịch vụ phụ trợ đã khởi động: ./dev.sh start (Cổng API: 22670)',
    'meta.missing': 'Thiếu nguồn dữ liệu',
    'meta.mock': 'Dữ liệu tham khảo',
    'meta.forecast': 'Dữ liệu dự báo',
    'meta.actual': 'Dữ liệu thực tế (Đã khử nhận dạng)',
    'meta.asOf': 'tính đến',
    'meta.source': 'nguồn',
    'footer.line1':
      'Thông tin trên trang web này chỉ mang tính tham khảo và không cấu thành lời khuyên đầu tư hoặc đề nghị.',
    'footer.line2':
      'Các tuyên bố hướng tới tương lai có tính không chắc chắn. Số tiền chính sách tùy thuộc vào sự phê duyệt cuối cùng của cơ quan có thẩm quyền.',
    'nav.otherSites': 'Các trang khác',
    'map.schematic': 'Chiếu tọa độ tỉ lệ tương đương (Không có bản đồ nền)',
    'map.north': 'N ↑',
  },
  th: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': 'รหัสเครดิตสังคมแบบรวม',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': 'ห้อง 209 เลขที่ 25 ถนนหงหูใต้ เขตหงเฉียว เทียนจิน',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': 'เปลี่ยนภาษา',
    'state.loading': 'กำลังโหลด…',
    'error.title': 'โหลดข้อมูลไม่สำเร็จ',
    'error.hint': 'โปรดตรวจสอบว่าระบบแบ็กเอนด์เริ่มทำงานแล้ว: ./dev.sh start (API พอร์ต 22670)',
    'meta.missing': 'ไม่มีการระบุแหล่งที่มาของข้อมูล',
    'meta.mock': 'ข้อมูลอ้างอิง',
    'meta.forecast': 'ข้อมูลคาดการณ์',
    'meta.actual': 'ข้อมูลจริง (นิรนาม)',
    'meta.asOf': 'ณ วันที่',
    'meta.source': 'แหล่งที่มา',
    'footer.line1':
      'ข้อมูลในเว็บไซต์นี้มีไว้เพื่อการอ้างอิงเท่านั้น และไม่ถือเป็นคำแนะนำในการลงทุนหรือข้อเสนอ',
    'footer.line2':
      'ข้อความที่มีลักษณะเป็นการคาดการณ์ในอนาคตมีความไม่แน่นอน จำนวนเงินนโยบายขึ้นอยู่กับการอนุมัติขั้นสุดท้ายจากหน่วยงานที่มีอำนาจ',
    'nav.otherSites': 'เว็บไซต์อื่นๆ ในเครือ',
    'map.schematic': 'การฉายพิกัดตามมาตราส่วนเท่ากัน (ไม่มีแผนที่ฐาน)',
    'map.north': 'N ↑',
  },
  id: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': 'Kode Kredit Sosial Terpadu',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': 'Kamar 209, Jalan Honghu Selatan No. 25, Distrik Hongqiao, Tianjin',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': 'Ganti bahasa',
    'state.loading': 'Memuat…',
    'error.title': 'Gagal memuat data',
    'error.hint': 'Pastikan backend telah berjalan: ./dev.sh start (Port API: 22670)',
    'meta.missing': 'Kaliber data hilang',
    'meta.mock': 'Data referensi',
    'meta.forecast': 'Data perkiraan',
    'meta.actual': 'Data aktual (Dianonimkan)',
    'meta.asOf': 'per',
    'meta.source': 'sumber',
    'footer.line1':
      'Informasi di situs web ini hanya untuk referensi dan bukan merupakan nasihat investasi atau penawaran.',
    'footer.line2':
      'Pernyataan berwawasan ke depan mengandung ketidakpastian. Jumlah kebijakan tunduk pada verifikasi akhir otoritas terkait.',
    'nav.otherSites': 'Situs lain',
    'map.schematic': 'Proyeksi koordinat skala sama (tanpa peta dasar)',
    'map.north': 'N ↑',
  },
  ms: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': 'Kod Kredit Sosial Bersepadu',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': 'Bilik 209, Jalan Honghu Selatan No. 25, Daerah Hongqiao, Tianjin',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': 'Tukar bahasa',
    'state.loading': 'Memuatkan…',
    'error.title': 'Gagal memuatkan data',
    'error.hint': 'Sila pastikan backend telah dimulakan: ./dev.sh start (Port API: 22670)',
    'meta.missing': 'Kaliber data hilang',
    'meta.mock': 'Data rujukan',
    'meta.forecast': 'Data ramalan',
    'meta.actual': 'Data sebenar (Dianonimkan)',
    'meta.asOf': 'setakat',
    'meta.source': 'sumber',
    'footer.line1':
      'Maklumat di laman web ini adalah untuk rujukan sahaja dan tidak membentuk nasihat pelaburan atau tawaran.',
    'footer.line2':
      'Kenyataan berpandangan ke hadapan melibatkan ketidakpastian. Jumlah dasar tertakluk kepada pengesahan akhir pihak berkuasa.',
    'nav.otherSites': 'Laman lain',
    'map.schematic': 'Unjuran koordinat skala sama (tiada peta asas)',
    'map.north': 'N ↑',
  },
  km: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': 'លេខកូដឥណទានសង្គមបង្រួបបង្រួម',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': 'បន្ទប់ 209 ផ្លូវ Honghu South លេខ 25 ស្រុក Hongqiao ទីក្រុងធានជីន',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': 'ប្តូរភាសា',
    'state.loading': 'កំពុងផ្ទុក…',
    'error.title': 'ការផ្ទុកទិន្នន័យបានបរាជ័យ',
    'error.hint': 'សូមបញ្ជាក់ថា backend ត្រូវបានចាប់ផ្តើម៖ ./dev.sh start (API port 22670)',
    'meta.missing': 'ខ្វះប្រភពទិន្នន័យ',
    'meta.mock': 'ទិន្នន័យយោង',
    'meta.forecast': 'ទិន្នន័យព្យាករណ៍',
    'meta.actual': 'ទិន្នន័យពិតប្រាកដ',
    'meta.asOf': 'គិតត្រឹម',
    'meta.source': 'ប្រភព',
    'footer.line1':
      'ព័ត៌មាននៅលើគេហទំព័រនេះគឺសម្រាប់ជាឯកសារយោងតែប៉ុណ្ណោះ ហើយមិនបង្កើតជាដំបូន្មានវិនិយោគនោះទេ។',
    'footer.line2':
      'សេចក្តីថ្លែងការណ៍ទាក់ទងនឹងអនាគតមានភាពមិនប្រាកដប្រជា។ ចំនួនទឹកប្រាក់គោលនយោបាយត្រូវស្ថិតក្រោមការអនុម័តចុងក្រោយ។',
    'nav.otherSites': 'គេហទំព័រផ្សេងទៀត',
    'map.schematic': 'ការព្យាករណ៍កូអរដោនេ (គ្មានផែនទីមូលដ្ឋាន)',
    'map.north': 'N ↑',
  },
  ja: {
    'legal.name': 'Tianjin Andi Culture Communication Co., Ltd.',
    'legal.taxLabel': '統一社会信用コード',
    'legal.taxId': '91120105MA06BLB346',
    'legal.address': '天津市紅橋区洪湖南路25号209室',
    'lang.zh': '中文',
    'lang.en': 'EN',
    'lang.vi': 'Tiếng Việt',
    'lang.th': 'ไทย',
    'lang.id': 'Bahasa Indonesia',
    'lang.ms': 'Bahasa Melayu',
    'lang.km': 'ភាសាខ្មែរ',
    'lang.ja': '日本語',
    'lang.switchTo': '言語切替',
    'state.loading': '読み込み中…',
    'error.title': 'データ読み込み失敗',
    'error.hint': 'バックエンドが起動していることを確認してください：./dev.sh start (APIポート: 22670)',
    'meta.missing': 'データ仕様未記載',
    'meta.mock': '参考データ',
    'meta.forecast': '予測データ',
    'meta.actual': '実績データ（匿名化済）',
    'meta.asOf': '時点',
    'meta.source': '情報源',
    'footer.line1':
      '当サイトの情報は参考用であり、投資助言や勧誘を構成するものではありません。将来予想に関する記述には不確実性が含まれます。',
    'footer.line2':
      '政策支援金の確定額は管轄当局の最終承認に準拠します。',
    'nav.otherSites': 'グループサイト一覧',
    'map.schematic': '等比率経緯度投影（背景地図なし）',
    'map.north': 'N ↑',
  },
};

/** [gemini-i18n-core] 多语言金额格式化，支持人民币及多国呈现标准 */
export function formatMoney(value: number, locale: Locale = 'zh'): string {
  const abs = Math.abs(value);
  if (locale === 'zh') {
    if (abs >= 1e8) return `${(value / 1e8).toFixed(2)} 亿元`;
    if (abs >= 1e4) return `${(value / 1e4).toFixed(1)} 万元`;
    return `${value.toFixed(0)} 元`;
  }
  if (locale === 'ja') {
    if (abs >= 1e8) return `RMB ${(value / 1e8).toFixed(2)} 億円`;
    if (abs >= 1e4) return `RMB ${(value / 1e4).toFixed(1)} 万元`;
    return `RMB ${value.toFixed(0)} 円`;
  }
  // 国际英文及东盟多语种规范 (en, vi, th, id, ms, km)
  if (abs >= 1e9) return `RMB ${(value / 1e9).toFixed(2)} bn`;
  if (abs >= 1e6) return `RMB ${(value / 1e6).toFixed(1)} m`;
  if (abs >= 1e3) return `RMB ${(value / 1e3).toFixed(0)}k`;
  return `RMB ${value.toFixed(0)}`;
}

export function formatRatio(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`;
}

export function formatInt(value: number, locale: Locale = 'zh'): string {
  const code = locale === 'zh' ? 'zh-CN' : locale === 'ja' ? 'ja-JP' : 'en-US';
  return new Intl.NumberFormat(code).format(value);
}

/** [gemini-i18n-core] 日期格式化，支持东盟与国际惯例 */
export function formatDate(iso: string, locale: Locale = 'zh'): string {
  if (!iso) return '';
  if (locale === 'zh' || locale === 'ja') return iso.slice(0, 10);
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
